import {useState} from 'react'
import {StyleSheet, TextInput, TouchableOpacity, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {usePalette} from '#/lib/hooks/usePalette'
import {Text} from '#/view/com/util/text/Text'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'

const flairs = [
  {id: 'none', label: 'No flair', value: null},
  {id: 'everyone', label: 'Everyone welcome', value: 'Everyone welcome'},
  {id: 'cam-mic', label: 'Cam & mic required', value: 'Cam & mic required'},
  {id: 'recording', label: 'Recording', value: 'Recording'},
]

interface CreateRoomDialogProps {
  control: Dialog.DialogControlProps
  onCreateRoom: (roomName: string, flair?: string) => Promise<any>
}

export function CreateRoomDialog({
  control,
  onCreateRoom,
}: CreateRoomDialogProps) {
  const pal = usePalette('default')
  const {_} = useLingui()
  const [roomName, setRoomName] = useState('')
  const [selectedFlairs, setSelectedFlairs] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleFlairToggle = (flairId: string) => {
    if (flairId === 'none') {
      // Clear all selections
      setSelectedFlairs([])
    } else {
      setSelectedFlairs(prev => {
        // Remove 'none' if present
        const withoutNone = prev.filter(id => id !== 'none')

        // Toggle the clicked flair
        if (withoutNone.includes(flairId)) {
          return withoutNone.filter(id => id !== flairId)
        } else {
          return [...withoutNone, flairId]
        }
      })
    }
  }

  const handleSubmit = async () => {
    if (!roomName.trim()) return

    setIsSubmitting(true)
    setError(null)

    try {
      // Convert selected flair IDs to their values
      const selectedFlairValues = selectedFlairs
        .map(id => flairs.find(f => f.id === id)?.value)
        .filter((v): v is string => v !== null && v !== undefined)

      // Join multiple flairs with comma
      const flairValue =
        selectedFlairValues.length > 0
          ? selectedFlairValues.join(',')
          : undefined

      await onCreateRoom(roomName.trim(), flairValue)

      // Only reset form and close dialog on success
      setRoomName('')
      setSelectedFlairs([])
      control.close()
    } catch (err: any) {
      // Handle errors and keep dialog open
      const errorMessage =
        err?.message || 'Failed to create room. Please try again.'
      setError(errorMessage)
      console.error('Failed to create room:', err)
    } finally {
      setIsSubmitting(false)
    }
  }

  const isValid = roomName.trim().length > 0

  return (
    <Dialog.Outer control={control}>
      <Dialog.Handle />
      <Dialog.ScrollableInner label={_(msg`Create Video Call Room`)}>
        <Text style={[styles.description, pal.text]}>
          <Trans>
            Create a video call room that other Bluesky users can join. Only
            logged in users can join your room.
          </Trans>
        </Text>

        <View style={styles.inputContainer}>
          <Text style={[styles.label, pal.text]}>
            <Trans>Room Name</Trans>
          </Text>
          <TextInput
            style={[styles.input, pal.border, pal.text]}
            value={roomName}
            onChangeText={setRoomName}
            placeholder={_(msg`Enter a name for your room`)}
            placeholderTextColor={pal.textLight.color}
            autoCapitalize="none"
            autoCorrect={false}
            maxLength={50}
            testID="createRoomNameInput"
            accessibilityLabel={_(msg`Room name input`)}
            accessibilityHint={_(msg`Enter the name for your new room`)}
          />
        </View>

        <View style={styles.inputContainer}>
          <Text style={[styles.label, pal.text]}>
            <Trans>Flair</Trans>
          </Text>
          <View style={styles.flairContainer}>
            {flairs.map(flair => {
              const isSelected =
                selectedFlairs.includes(flair.id) ||
                (flair.id === 'none' && selectedFlairs.length === 0)

              return (
                <TouchableOpacity
                  key={flair.id}
                  style={styles.flairOption}
                  onPress={() => handleFlairToggle(flair.id)}
                  testID={`flair-${flair.id}`}
                  accessibilityRole="radio"
                  accessibilityState={{selected: isSelected}}
                  accessibilityLabel={flair.label}
                  accessibilityHint={`Select ${flair.label} as room flair`}>
                  <View
                    style={[
                      styles.radioButton,
                      isSelected && styles.radioButtonSelected,
                    ]}>
                    {isSelected && (
                      <View style={[styles.radioButtonInner, pal.text]} />
                    )}
                  </View>
                  {flair.id === 'none' ? (
                    <Text style={[styles.flairText, pal.text]}>
                      {flair.label}
                    </Text>
                  ) : (
                    <View
                      style={[
                        styles.flairPill,
                        flair.id === 'everyone' && styles.flairPillEveryone,
                        flair.id === 'cam-mic' && styles.flairPillCamMic,
                        flair.id === 'recording' && styles.flairPillRecording,
                      ]}>
                      <Text
                        style={[
                          styles.flairPillText,
                          flair.id === 'everyone' &&
                            styles.flairPillTextEveryone,
                          flair.id === 'cam-mic' && styles.flairPillTextCamMic,
                          flair.id === 'recording' &&
                            styles.flairPillTextRecording,
                        ]}>
                        {flair.label}
                      </Text>
                    </View>
                  )}
                </TouchableOpacity>
              )
            })}
          </View>
        </View>

        {error && (
          <View style={styles.errorContainer}>
            <Text style={[styles.errorText, pal.textLight]}>{error}</Text>
          </View>
        )}

        <View style={styles.buttonsContainer}>
          <Button
            label={_(msg`Cancel`)}
            variant="ghost"
            color="secondary"
            size="small"
            onPress={() => control.close()}
            disabled={isSubmitting}>
            <ButtonText>
              <Trans>Cancel</Trans>
            </ButtonText>
          </Button>

          <Button
            label={_(msg`Create Room`)}
            variant="solid"
            color="primary"
            size="small"
            onPress={handleSubmit}
            disabled={!isValid || isSubmitting}
            testID="createRoomSubmitBtn">
            <ButtonText>
              <Trans>Create Room</Trans>
            </ButtonText>
          </Button>
        </View>

        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}

const styles = StyleSheet.create({
  description: {
    marginBottom: 16,
  },
  inputContainer: {
    marginBottom: 16,
  },
  label: {
    marginBottom: 8,
    fontWeight: 'bold',
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
  },
  buttonsContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 16,
  },
  flairContainer: {
    gap: 12,
  },
  flairOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: '#666',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioButtonSelected: {
    borderColor: '#007AFF',
  },
  radioButtonInner: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#007AFF',
  },
  flairText: {
    fontSize: 16,
  },
  flairPill: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  flairPillEveryone: {
    backgroundColor: '#F0F0F0',
  },
  flairPillCamMic: {
    backgroundColor: '#FF6B47',
  },
  flairPillRecording: {
    backgroundColor: '#FF3B30',
  },
  flairPillText: {
    fontSize: 16,
    fontWeight: '500',
  },
  flairPillTextEveryone: {
    color: '#333',
  },
  flairPillTextCamMic: {
    color: '#FFF',
  },
  flairPillTextRecording: {
    color: '#FFF',
  },
  errorContainer: {
    marginTop: 16,
    padding: 12,
    backgroundColor: '#FFE6E6',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FF6B47',
  },
  errorText: {
    fontSize: 14,
    color: '#CC0000',
    textAlign: 'center',
  },
})
